import React, { FC, useState } from "react";
import { IAcompanhamento, IEscola, IMatricula } from "@deltasge/marauders-map";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { Printer as PrintIcon } from "mdi-material-ui";
import { Avatar } from "components/Avatar";

const Cabecalho: FC<{ escola: IEscola }> = ({ escola }) => {
  const localidade = [escola.cidade, escola.estado]
    .filter((valor) => !!valor)
    .join(" - ");
  const linhas = [
    escola.endereco,
    escola.bairro,
    localidade,
    escola.cep ? `CEP: ${escola.cep}` : undefined,
    escola.fone ? `Fone: ${escola.fone}` : undefined,
  ].filter((valor) => !!valor);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: 22, fontWeight: "bold" }}>
        {escola.nome}
      </Typography>
      {linhas.map((linha) => (
        <Typography key={linha} sx={{ fontSize: 12 }}>
          {linha}
        </Typography>
      ))}
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
};

const Blocos: FC<{
  pareceres: IAcompanhamento[];
  vazio: string;
}> = ({ pareceres, vazio }) => {
  const registros = pareceres.filter(
    (parecer) => (parecer.texto ?? "").trim().length > 0
  );

  if (registros.length === 0) {
    return (
      <Typography
        sx={{ fontSize: 13, color: "#666", py: 4, textAlign: "center" }}
      >
        {vazio}
      </Typography>
    );
  }

  return (
    <>
      {registros.map((parecer) => (
        <Box
          key={`impressao_parecer_${parecer.id}`}
          className="bloco-parecer"
          sx={{ mb: 3 }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: "bold",
              textTransform: "uppercase",
              color: "#444",
              mb: 0.5,
            }}
          >
            {parecer.materia?.nome}
          </Typography>
          <Typography
            sx={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}
          >
            {parecer.texto}
          </Typography>
        </Box>
      ))}
    </>
  );
};

export const Impressao: FC<{
  open: boolean;
  onClose: () => void;
  tipo?: "turma" | "aluno";
  escola: IEscola;
  turma?: string;
  etapa?: number;
  nomeEtapa: string;
  matricula?: IMatricula;
  pareceres: IAcompanhamento[];
}> = ({
  open,
  onClose,
  tipo,
  escola,
  turma,
  etapa,
  nomeEtapa,
  matricula: matriculaAluno,
  pareceres,
}) => {
  const [exibirFoto, setExibirFoto] = useState(false);

  const nomeAluno =
    matriculaAluno?.aluno?.nomeusual || matriculaAluno?.aluno?.nome || "";
  const periodo = etapa != undefined ? `${etapa}º ${nomeEtapa}` : nomeEtapa;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ className: "acompanhamento-impressao" }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box id="printable-area">
          <Cabecalho escola={escola} />

          {tipo === "aluno" ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                }}
              >
                {exibirFoto && (
                  <Avatar
                    displayName={nomeAluno}
                    src={
                      matriculaAluno?.aluno?.imagemAluno?.caminhoArquivo ??
                      undefined
                    }
                    thumbnail={false}
                    sx={{
                      width: 85,
                      height: 110,
                      borderRadius: 0,
                      border: "1px solid #999",
                      flexShrink: 0,
                    }}
                  />
                )}
                <Box
                  sx={{
                    flexGrow: 1,
                    textAlign: exibirFoto ? "left" : "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: "bold",
                      textTransform: "uppercase",
                    }}
                  >
                    Relatório Único de Acompanhamento
                  </Typography>
                  <Typography sx={{ fontSize: 15, mt: 0.5 }}>
                    {periodo}
                    {turma ? ` | Turma: ${turma}` : ""} | Aluno(a):{" "}
                    <strong>{nomeAluno}</strong>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#555" }}>
                    Pareceres de Todos os Professores
                  </Typography>
                </Box>
              </Box>

              <Blocos
                pareceres={pareceres}
                vazio="Nenhum parecer individual registrado para este aluno até o momento."
              />
            </>
          ) : (
            <>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                  }}
                >
                  Acompanhamento Pedagógico Descritivo — Análise Coletiva
                </Typography>
                <Typography sx={{ fontSize: 14 }}>
                  {turma ? `${turma} | ` : ""}
                  {periodo}
                </Typography>
              </Box>

              <Blocos
                pareceres={pareceres}
                vazio="Nenhum parecer coletivo registrado para esta turma."
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        className="no-print"
        sx={{ p: 2, bgcolor: "#f5f5f5", borderTop: "1px solid #e0e0e0" }}
      >
        {tipo === "aluno" && (
          <FormControlLabel
            sx={{
              mr: "auto",
              "& .MuiTypography-root": { fontSize: "0.85rem", fontWeight: 500 },
            }}
            control={
              <Checkbox
                size="small"
                color="primary"
                checked={exibirFoto}
                onChange={(event) => setExibirFoto(event.target.checked)}
              />
            }
            label="Exibir foto do aluno no cabeçalho do relatório"
          />
        )}
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ ml: tipo === "aluno" ? 0 : "auto" }}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
        >
          Imprimir Documento
        </Button>
      </DialogActions>
    </Dialog>
  );
};
